import { defineComponent as _defineComponent } from 'vue'
import { withModifiers as _withModifiers, withKeys as _withKeys, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "retailer-search-grid" }
const _hoisted_2 = { class: "wrapper" }
const _hoisted_3 = ["value", "onKeydown"]
const _hoisted_4 = {
  key: 0,
  class: "suggestions"
}
const _hoisted_5 = ["onMousedown"]
const _hoisted_6 = { class: "logo-wrapper" }
const _hoisted_7 = {
  key: 0,
  class: "logo-skeleton"
}
const _hoisted_8 = ["src", "alt", "onLoad", "onError"]
const _hoisted_9 = { class: "retailer-info" }
const _hoisted_10 = { key: 0 }

import { ref, computed, onMounted } from "vue";
import MerchantService from "../services/merchant-service";
import { useTenantConfig } from "../composables/useTenantConfig";

interface Retailer {
  id: number;
  name: string;
  urlName: string;
  includeKeywords: string[];
  categories: string[];
  imageUrl?: string;
  priorityOrder: number;
}
const defaultImage =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect width="200" height="200" fill="%23cccccc"/></svg>';

const cdnPrefix = "https://cdn.rewards4sport.com/cdn-cgi/image/width=100,format=auto,quality=90,fit=scale-down/";

// The CDN resizer can only fetch/transform images hosted on
// *.rewards4sport.com. Many merchant logos are hosted by third parties, so
// Cache the CDN-eligibility check per URL so we don't re-parse the same
// URL on every keystroke re-render.
const MAX_SUGGESTIONS = 8;

// Keywords/categories are often short root words (e.g. "bet"). A plain
// `tag.includes(query)` check fails once the user types past that root
// (e.g. "betting"), even though the tag is clearly still relevant. Matching
// in either direction keeps short tags matching longer, related queries.
// Guard against empty/very short tags in the reverse direction though -
// otherwise something like an empty string or single-letter tag would
// trivially match every query (query.includes("") is always true).
const MIN_REVERSE_MATCH_LENGTH = 3;

export default /*@__PURE__*/_defineComponent({
  __name: 'SearchBar',
  setup(__props) {

const { settings } = useTenantConfig();
const query = ref("");
const retailerData = ref<Retailer[]>([]);
const highlightedIndex = ref(-1);
const loadedImageIds = ref<Set<string>>(new Set());

// Inline 200x200 grey SVG as fallback
const cdnEligibilityCache = new Map<string, boolean>();
function isRewards4SportUrl(url: string) {
  const cached = cdnEligibilityCache.get(url);
  if (cached !== undefined) return cached;
  let result: boolean;
  try {
    const { hostname } = new URL(url);
    result = hostname === "rewards4sport.com" || hostname.endsWith(".rewards4sport.com");
  } catch {
    result = false;
  }
  cdnEligibilityCache.set(url, result);
  return result;
}

// Suggestion thumbnails are rendered at ~50px tall, so request a small,
// auto-formatted image from the CDN resizer (2x for retina) instead of the
// full-size original where possible.
function getOptimizedImageUrl(url?: string) {
  if (!url) return defaultImage;
  // The backend may already have wrapped this url with the CDN resizer
  // (e.g. CustomPartnerPage/BettingPartnerPage logos). Wrapping it again
  // produces an invalid nested url that fails to load.
  if (url.includes("/cdn-cgi/image/")) return url;
  return isRewards4SportUrl(url) ? `${cdnPrefix}${url}` : url;
}

async function loadRetailers() {
  try {
    const siteId = settings.value?.tenantSettings?.find((x) => x.name === "CmsSiteId")?.value ?? "";
    const applicationName = settings.value?.tenantSettings?.find((x) => x.name === "ApplicationName")?.value ?? "";
    const clubId = settings.value?.tenantSettings?.find((x) => x.name === "ClubId")?.value ??"";

    const merchants = await MerchantService.getSearchMerchantsData(siteId, applicationName, clubId);

    retailerData.value = merchants.merchants.map((m) => ({
      id: m.merchantId,
      name: m.name,
      urlName: m.urlName,
      includeKeywords: m.includeKeywords || [],
      categories: m.categories || [],
      imageUrl: m.imageUrl || undefined,
      priorityOrder: m.priorityOrder,
    }));
  } catch (err) {
    console.error("Failed to load merchants:", err);
  }
}

onMounted(() => {
  loadRetailers();
});

// Clears the input field
function clearSearch() {
  query.value = ""; // Clear the input
  highlightedIndex.value = -1; // Reset highlighted suggestion
}

// Cap the number of rendered suggestions - beyond this there's no visible
// benefit and it only adds unnecessary DOM/image work per keystroke.
function isRelatedMatch(tag: string, query: string) {
  const t = tag.trim().toLowerCase();
  if (!t) return false;
  if (t.includes(query)) return true;
  return t.length >= MIN_REVERSE_MATCH_LENGTH && query.includes(t);
}

const filteredRetailers = computed(() => {
  if (!query.value) return [];
  const q = query.value.toLowerCase();

  const nameMatches: Retailer[] = [];
  const keywordMatches: Retailer[] = [];
  const categoryMatches: Retailer[] = [];
  // Dedupe by urlName rather than id - merchantId can be 0/duplicated across
  // merchants (e.g. test data), but urlName is guaranteed unique per merchant.
  const seenUrlNames = new Set<string>();

  for (const r of retailerData.value) {
    if (r.name.toLowerCase().includes(q)) {
      nameMatches.push(r);
      seenUrlNames.add(r.urlName);
    }
  }
  for (const r of retailerData.value) {
    if (!seenUrlNames.has(r.urlName) && r.includeKeywords.some((k) => isRelatedMatch(k, q))) {
      keywordMatches.push(r);
      seenUrlNames.add(r.urlName);
    }
  }
  for (const r of retailerData.value) {
    if (!seenUrlNames.has(r.urlName) && r.categories.some((c) => isRelatedMatch(c, q))) {
      categoryMatches.push(r);
      seenUrlNames.add(r.urlName);
    }
  }

  const merged = [...nameMatches, ...keywordMatches, ...categoryMatches];
  return merged
    .sort((a, b) => a.priorityOrder - b.priorityOrder)
    .slice(0, MAX_SUGGESTIONS);
});

function onSearch(event: Event) {
  // Read the value directly from the native input event rather than relying
  // on v-model, which defers updates until IME composition ends. Android
  // keyboards (e.g. Gboard) treat plain English words as "composing" spans
  // for inline autocorrect, so v-model wouldn't update per-keystroke and the
  // live suggestions would only refresh once a word/space was completed.
  const target = event.target as HTMLInputElement;
  query.value = target.value;
  highlightedIndex.value = -1;
}

function onArrowDown() {
  if (!filteredRetailers.value.length) return;
  highlightedIndex.value =
    (highlightedIndex.value + 1) % filteredRetailers.value.length;
}

function onArrowUp() {
  if (!filteredRetailers.value.length) return;
  highlightedIndex.value =
    highlightedIndex.value <= 0
      ? filteredRetailers.value.length - 1
      : highlightedIndex.value - 1;
}

function onEnter() {
  // Only trigger if a suggestion is selected
  if (
    highlightedIndex.value >= 0 &&
    filteredRetailers.value[highlightedIndex.value]
  ) {
    selectRetailer(filteredRetailers.value[highlightedIndex.value]);
  }
  // Otherwise, do nothing when pressing Enter
}

function selectRetailer(retailer: Retailer) {
  window.location.href = `${retailer.urlName}`;
}

function onImageLoad(urlName: string) {
  loadedImageIds.value.add(urlName);
}

function onImageError(event: Event, urlName: string) {
  const target = event.target as HTMLImageElement | null;
  if (target) {
    target.src = defaultImage;
  }
  loadedImageIds.value.add(urlName);
}

function onSubmit() {
  if (filteredRetailers.value.length > 0) {
    window.location.href = `${filteredRetailers.value[0].urlName}`;
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("form", {
      onSubmit: _withModifiers(onSubmit, ["prevent"])
    }, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("input", {
          value: query.value,
          type: "text",
          id: "retailer-search",
          placeholder: "Search for a brand",
          autocomplete: "off",
          onInput: onSearch,
          onKeydown: [
            _withKeys(_withModifiers(onArrowDown, ["prevent"]), ["down"]),
            _withKeys(_withModifiers(onArrowUp, ["prevent"]), ["up"]),
            _withKeys(_withModifiers(onEnter, ["prevent"]), ["enter"])
          ]
        }, null, 40, _hoisted_3),
        _createElementVNode("span", {
          class: _normalizeClass(["material-symbols-outlined search-icon search", { visible: !query.value.length }])
        }, " search ", 2),
        _createElementVNode("span", {
          class: _normalizeClass(["material-symbols-outlined search-icon close", { visible: query.value.length }]),
          onClick: _cache[0] || (_cache[0] = ($event: any) => (clearSearch()))
        }, " close ", 2),
        (query.value && filteredRetailers.value.length > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredRetailers.value, (retailer, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: retailer.urlName,
                  class: _normalizeClass(['suggestion-item', { active: index === highlightedIndex.value }]),
                  onMousedown: _withModifiers(($event: any) => (selectRetailer(retailer)), ["prevent"])
                }, [
                  _createElementVNode("div", _hoisted_6, [
                    (!loadedImageIds.value.has(retailer.urlName))
                      ? (_openBlock(), _createElementBlock("div", _hoisted_7))
                      : _createCommentVNode("", true),
                    (_openBlock(), _createElementBlock("img", {
                      key: retailer.urlName,
                      src: getOptimizedImageUrl(retailer.imageUrl),
                      alt: retailer.name,
                      width: "50",
                      height: "50",
                      loading: "eager",
                      decoding: "async",
                      class: _normalizeClass({ loaded: loadedImageIds.value.has(retailer.urlName) }),
                      onLoad: ($event: any) => (onImageLoad(retailer.urlName)),
                      onError: ($event: any) => (onImageError($event, retailer.urlName))
                    }, null, 42, _hoisted_8))
                  ]),
                  _createElementVNode("div", _hoisted_9, [
                    _createElementVNode("strong", null, _toDisplayString(retailer.name), 1),
                    (retailer.categories.length)
                      ? (_openBlock(), _createElementBlock("em", _hoisted_10, " - " + _toDisplayString(retailer.categories[0]), 1))
                      : _createCommentVNode("", true)
                  ])
                ], 42, _hoisted_5))
              }), 128))
            ]))
          : _createCommentVNode("", true)
      ])
    ], 32)
  ]))
}
}

})